// src/pages/workflow/ICPConfirmation.js
import React, { useRef, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Alert,
  TextField,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Select,
  MenuItem
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

const ALLOWED_SENIORITY_LEVELS = [
  'cxo',
  'director',
  'manager',
  'owner',
  'partner',
  'senior',
  'vp',
];

const formatSeniorityLabel = (value) => {
  const s = String(value || '')
    .toLowerCase()
    .trim();
  if (!s) return '';
  if (s === 'cxo') return 'CxO';
  if (s === 'vp') return 'VP';
  return s.charAt(0).toUpperCase() + s.slice(1);
};

/**
 * Component for displaying ICP data and asking for confirmation
 * @param {Object} props
 * @param {Object} props.icpData - ICP data from the API response
 * @param {string} props.rephrase - Rephrased intent
 * @param {Function} props.onConfirm - Callback when user confirms
 * @param {Function} props.onCancel - Callback when user cancels
 * @param {boolean} props.loading - Loading state
 * @param {number} props.remainingCredits - User's remaining credits
 */
function ICPConfirmation({ icpData, onConfirm, onCancel, loading, remainingCredits = 0 }) {
  const { icp, clusters_for_sourcing_pro: clusters } = icpData || {};
  const sanitizeSeniority = (values) => {
    if (!Array.isArray(values)) return [];
    return values
      .map((value) => String(value || '').toLowerCase().trim())
      .filter((value) => ALLOWED_SENIORITY_LEVELS.includes(value));
  };

  const [editableClusters, setEditableClusters] = useState(() => ({
    titles: Array.isArray(clusters?.titles) ? clusters.titles : [],
    organisations: Array.isArray(clusters?.organisations) ? clusters.organisations : [],
    skills: Array.isArray(clusters?.skills) ? clusters.skills : [],
    experience_year_patterns: Array.isArray(clusters?.experience_year_patterns)
      ? clusters.experience_year_patterns
      : [],
    locations: Array.isArray(clusters?.locations) ? clusters.locations : [],
    seniority: sanitizeSeniority(clusters?.seniority),
  }));

  const [newValues, setNewValues] = useState({
    titles: '',
    organisations: '',
    skills: '',
    experience_year_patterns: '',
    locations: '',
  });

  const [creditsDialogOpen, setCreditsDialogOpen] = useState(false);
  const [resultsDialogOpen, setResultsDialogOpen] = useState(false);
  const [selectedResultsCount, setSelectedResultsCount] = useState(10);
  const [resultsError, setResultsError] = useState(null);
  const [seniorityError, setSeniorityError] = useState(null);
  const hasSenioritySelection = (editableClusters.seniority || []).length > 0;
  const senioritySelectRef = useRef(null);

  const handleAddValue = (field) => {
    const raw = (newValues[field] || '').trim();
    if (!raw) return;
    const pieces = raw.split(',').map((p) => p.trim()).filter(Boolean);
    if (pieces.length === 0) return;

    setEditableClusters((prev) => ({
      ...prev,
      [field]: [...(prev[field] || []), ...pieces],
    }));
    setNewValues((prev) => ({ ...prev, [field]: '' }));
  };

  const handleDeleteValue = (field, index) => {
    setEditableClusters((prev) => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index),
    }));
  };

  return (
    <Box sx={{ mx: 'auto', p: 1 }}>
      <Typography variant="h4" sx={{ fontWeight: 600 }}>
        Generated ICP
      </Typography>
      <Typography variant="body1" sx={{ color: '#666', mb: 2 }}>
        Please review the generated Ideal Customer Profile below and confirm to proceed.
      </Typography>

      {remainingCredits < 20 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You currently have {remainingCredits} credits. You need at least 20 credits to run this search.
        </Alert>
      )}

      {!icp && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No ICP data available
        </Alert>
      )}

      {/* ICP Data (single section with required fields) */}
      {icp && (
        <Card sx={{ boxShadow: 3, mb: 2 }}>
          <CardContent>
            {typeof icp.who_is_this_person === 'string' &&
            icp.who_is_this_person.trim().length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Who is this person?
                </Typography>
                <Typography variant="body1" sx={{ color: '#444', lineHeight: 1.7 }}>
                  {icp.who_is_this_person}
                </Typography>
              </Box>
            )}

            {typeof icp.what_will_they_do === 'string' &&
            icp.what_will_they_do.trim().length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                  What will they do?
                </Typography>
                <Typography variant="body1" sx={{ color: '#444', lineHeight: 1.7 }}>
                  {icp.what_will_they_do}
                </Typography>
              </Box>
            )}

            {typeof icp.must_have_skills === 'string' &&
            icp.must_have_skills.trim().length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Must have skills
                </Typography>
                <Typography variant="body1" sx={{ color: '#444', lineHeight: 1.7 }}>
                  {icp.must_have_skills}
                </Typography>
              </Box>
            )}

            {typeof icp.summary === 'string' && icp.summary.trim().length > 0 && (
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                Summary
              </Typography>
              <Typography variant="body1" sx={{ color: '#444', lineHeight: 1.7 }}>
                {icp.summary}
              </Typography>
            </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Search Cluster (editable) */}
      {clusters && (
        <Card sx={{ boxShadow: 3, mb: 1 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              Search Cluster
            </Typography>

            {/* Seniority */}
            <Box sx={{ mb: 2 }}>
              <FormControl size="small" sx={{ minWidth: 300 }} error={!!seniorityError}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, pr: 1, mb: 1 }}>Select seniority *</Typography>
                <Select
                  labelId="seniority-select-label"
                  variant='outlined'
                  multiple
                  inputRef={senioritySelectRef}
                  value={editableClusters.seniority || []}
                  label="Select seniority *"
                  onChange={(e) => {
                    const selectedValues = sanitizeSeniority(e.target.value);
                    setEditableClusters((prev) => ({
                      ...prev,
                      seniority: selectedValues,
                    }));
                    if (selectedValues.length > 0) {
                      setSeniorityError(null);
                    }
                  }}
                  renderValue={(selected) => selected.map(formatSeniorityLabel).join(', ')}
                >
                  {ALLOWED_SENIORITY_LEVELS.map((level) => (
                    <MenuItem key={level} value={level}>
                      {formatSeniorityLabel(level)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {seniorityError && (
                <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                  {seniorityError}
                </Typography>
              )}
            </Box>

            {/* Titles — keep row visible when empty so users can re-add values */}
            {Array.isArray(editableClusters.titles) && (
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, pr: 1 }}
                  >
                    Titles : 
                  </Typography>
                  <TextField
                size="small"
                label="Add title"
                sx={{
                  borderRadius: 50,
                  maxWidth: 1500,
                  '& .MuiInputBase-input': { fontSize: 10 },
                  '& .MuiInputLabel-root': { fontSize: 10 },
                }}
                value={newValues.titles}
                onChange={(e) =>
                  setNewValues((prev) => ({ ...prev, titles: e.target.value }))
                }
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddValue('titles');
                  }
                }}
              /> 
                  {editableClusters.titles.map((title, idx) => (
                    <Chip
                      key={idx}
                      label={title}
                      onDelete={() => handleDeleteValue('titles', idx)}
                      sx={{
                        bgcolor: '#e3f2fd',
                        color: '#1976d2',
                        fontWeight: 500,
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Organisations */}
            {Array.isArray(editableClusters.organisations) && (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, pr: 1 }}
                  >
                    Organisations : 
                  </Typography>
                  <TextField
                    size="small"
                    label="Add organisations"
                    sx={{
                      borderRadius: 50,
                      maxWidth: 1500,
                      '& .MuiInputBase-input': { fontSize: 10 },
                      '& .MuiInputLabel-root': { fontSize: 10 },
                    }}
                    value={newValues.organisations}
                    onChange={(e) =>
                      setNewValues((prev) => ({ ...prev, organisations: e.target.value }))
                    }
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddValue('organisations');
                      }
                    }}
                  />
                    {editableClusters.organisations.map((org, idx) => (
                      <Chip
                        key={idx}
                        label={org}
                        onDelete={() => handleDeleteValue('organisations', idx)}
                        sx={{
                          bgcolor: '#fff3e0',
                          color: '#e65100',
                          fontWeight: 500,
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

            {/* Skills */}
            {Array.isArray(editableClusters.skills) && (
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, pr: 1 }}
                  >
                    Skills : 
                  </Typography>
                  <TextField
                    size="small"
                    label="Add skill"
                    sx={{
                      borderRadius: 50,
                      maxWidth: 1500,
                      '& .MuiInputBase-input': { fontSize: 10 },
                      '& .MuiInputLabel-root': { fontSize: 10 },
                    }}
                    value={newValues.skills}
                    onChange={(e) =>
                      setNewValues((prev) => ({ ...prev, skills: e.target.value }))
                    }
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddValue('skills');
                      }
                    }}
                  />
                  {editableClusters.skills.map((skill, idx) => (
                    <Chip
                      key={idx}
                      label={skill}
                      onDelete={() => handleDeleteValue('skills', idx)}
                      sx={{
                        bgcolor: '#e8f5e9',
                        color: '#2e7d32',
                        fontWeight: 500,
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}
            {/* <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <TextField
                size="small"
                label="Add skill"
                value={newValues.skills}
                onChange={(e) =>
                  setNewValues((prev) => ({ ...prev, skills: e.target.value }))
                }
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddValue('skills');
                  }
                }}
              />
              <IconButton
                color="primary"
                onClick={() => handleAddValue('skills')}
                disabled={loading}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Stack> */}

            {/* Experience year patterns (read-only) */}
            {Array.isArray(editableClusters.experience_year_patterns) &&
              editableClusters.experience_year_patterns.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, pr: 1 }}
                    >
                      Experience Patterns
                    </Typography>
                    
                    {editableClusters.experience_year_patterns.map((pattern, idx) => (
                      <Chip
                        key={idx}
                        label={pattern == null ? 'Any' : pattern}
                        sx={{
                          bgcolor: '#f3e5f5',
                          color: '#7b1fa2',
                          fontWeight: 500,
                        }}
                      />
                    ))}
                  </Box>
                  <Typography sx={{ fontSize: 12, color: '#666' }}>
                      Note: Heads up! Getlista curates decision makers with 10+ years of experience only.
                    </Typography>
                </Box>
              )}

            {/* Locations (read-only) */}
            {Array.isArray(editableClusters.locations) &&
              editableClusters.locations.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, pr: 1 }}
                    >
                      Locations
                    </Typography>
                    {editableClusters.locations.map((location, idx) => (
                      <Chip
                        key={idx}
                        label={location}
                        sx={{
                          bgcolor: '#e0f7fa',
                          color: '#006064',
                          fontWeight: 500,
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

          </CardContent>
        </Card>
      )}

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          onClick={onCancel}
          disabled={loading}
          startIcon={<CancelIcon />}
          sx={{
            borderColor: '#f44336',
            color: '#f44336',
            '&:hover': { borderColor: '#d32f2f', bgcolor: '#ffebee' }
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            if (!hasSenioritySelection) {
              setSeniorityError('select seniority');
              senioritySelectRef.current?.focus();
              return;
            }
            if (remainingCredits < 20) {
              setCreditsDialogOpen(true);
              return;
            }
            setResultsError(null);
            setSelectedResultsCount(10);
            setResultsDialogOpen(true);
          }}
          disabled={loading}
          startIcon={<CheckCircleIcon />}
          sx={{
            bgcolor: remainingCredits < 20 ? '#bdbdbd' : '#1BE885',
            color: remainingCredits < 20 ? '#555' : '#000',
            fontWeight: 600,
            '&:hover': {
              bgcolor: remainingCredits < 20 ? '#bdbdbd' : '#20ec84',
            },
            '&:disabled': { bgcolor: '#ccc' }
          }}
        >
          {loading ? 'Processing...' : 'Confirm & Continue'}
        </Button>
      </Box>

      <Dialog
        open={creditsDialogOpen}
        onClose={() => setCreditsDialogOpen(false)}
      >
        <DialogTitle>Not enough credits</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mt: 1 }}>
            You need at least 20 credits to run this search. You currently have {remainingCredits} credits.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreditsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={resultsDialogOpen}
        onClose={() => setResultsDialogOpen(false)}
      >
        <DialogTitle>Select number of results</DialogTitle>
        <DialogContent>

          <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
            {[10, 30, 50, 100].map((val) => (
              <Button
                key={val}
                variant={selectedResultsCount === val ? 'contained' : 'outlined'}
                size="small"
                onClick={() => {
                  setSelectedResultsCount(val);
                  setResultsError(null);
                }}
              >
                {val}
              </Button>
            ))}
          </Stack>
          {resultsError && (
            <Typography color="error" variant="body2" sx={{ mt: 2 }}>
              {resultsError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResultsDialogOpen(false)}>Cancel</Button>
          <Button
            disabled={!hasSenioritySelection}
            onClick={() => {
              const N = selectedResultsCount || 10;
              const requiredCredits = N * 2;
              const hasOnlyAllowedSeniority = (editableClusters.seniority || []).every((value) =>
                ALLOWED_SENIORITY_LEVELS.includes(String(value || '').toLowerCase().trim())
              );

              if (remainingCredits < requiredCredits) {
                setResultsError(
                  `You need ${requiredCredits} credits for ${N} results, but you only have ${remainingCredits}.`
                );
                return;
              }
              if (!hasOnlyAllowedSeniority) {
                setSeniorityError('Please choose seniority only from the allowed dropdown values.');
                return;
              }
              if ((editableClusters.seniority || []).length === 0) {
                setSeniorityError('Please select at least one seniority value.');
                return;
              }

              setResultsDialogOpen(false);
              setResultsError(null);
              setSeniorityError(null);
              onConfirm(editableClusters, N);
            }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ICPConfirmation;
